import React, { Fragment, useState, useEffect } from 'react';
import { indicatorsApi, usersApi, processesApi } from '../api';
import ExportButtons from '../components/ExportButtons';
import IndicatorPlanning from '../components/IndicatorPlanning';
import ColumnSelector from '../components/ColumnSelector';
import useColumnVisibility from '../hooks/useColumnVisibility';
import { toast } from 'sonner';
import { extractApiError } from '../utils/dateValidation';
import {
  RefreshCw,
  Plus,
  Search,
  Edit,
  Trash2,
  X,
  Loader2,
  TrendingUp,
  TrendingDown,
  Minus,
  Target,
  BarChart3,
  Eye,
  CheckCircle,
  AlertTriangle,
  XCircle,
} from 'lucide-react';

const INDICATOR_TYPES = [
  { value: 'quality', label: 'Qualidade' },
  { value: 'productivity', label: 'Produtividade' },
  { value: 'efficiency', label: 'Eficiência' },
  { value: 'customer', label: 'Cliente' },
  { value: 'process', label: 'Processo' },
  { value: 'financial', label: 'Financeiro' },
  { value: 'safety', label: 'Segurança' },
  { value: 'environmental', label: 'Ambiental' },
  { value: 'other', label: 'Outros' },
];

const FREQUENCIES = [
  { value: 'daily', label: 'Diário' },
  { value: 'weekly', label: 'Semanal' },
  { value: 'biweekly', label: 'Quinzenal' },
  { value: 'monthly', label: 'Mensal' },
  { value: 'quarterly', label: 'Trimestral' },
  { value: 'semiannual', label: 'Semestral' },
  { value: 'annual', label: 'Anual' },
];

const PERIOD_MODES = [
  { value: 'custom', label: 'Personalizado' },
  { value: 'month', label: 'Mês' },
  { value: 'quarter', label: 'Trimestre' },
  { value: 'semester', label: 'Semestre' },
  { value: 'year', label: 'Ano' },
];

const MONTHS = [
  { value: 1, label: 'Janeiro' }, { value: 2, label: 'Fevereiro' }, { value: 3, label: 'Março' },
  { value: 4, label: 'Abril' }, { value: 5, label: 'Maio' }, { value: 6, label: 'Junho' },
  { value: 7, label: 'Julho' }, { value: 8, label: 'Agosto' }, { value: 9, label: 'Setembro' },
  { value: 10, label: 'Outubro' }, { value: 11, label: 'Novembro' }, { value: 12, label: 'Dezembro' },
];

const QUARTERS = [
  { value: 1, label: '1º Trimestre (Jan-Mar)' },
  { value: 2, label: '2º Trimestre (Abr-Jun)' },
  { value: 3, label: '3º Trimestre (Jul-Set)' },
  { value: 4, label: '4º Trimestre (Out-Dez)' },
];

const SEMESTERS = [
  { value: 1, label: '1º Semestre (Jan-Jun)' },
  { value: 2, label: '2º Semestre (Jul-Dez)' },
];

const pad2 = (n) => String(n).padStart(2, '0');

/** Computes the { start, end } (YYYY-MM-DD) date range for a preset period mode. */
const getPeriodRange = (mode, year, unit) => {
  let startMonth, endMonth;
  switch (mode) {
    case 'month':
      startMonth = endMonth = unit;
      break;
    case 'quarter':
      startMonth = (unit - 1) * 3 + 1;
      endMonth = startMonth + 2;
      break;
    case 'semester':
      startMonth = (unit - 1) * 6 + 1;
      endMonth = startMonth + 5;
      break;
    case 'year':
      startMonth = 1;
      endMonth = 12;
      break;
    default:
      return null;
  }
  const lastDay = new Date(year, endMonth, 0).getDate();
  return {
    start: `${year}-${pad2(startMonth)}-01`,
    end: `${year}-${pad2(endMonth)}-${pad2(lastDay)}`,
  };
};

const TRENDS = [
  { value: 'increase', label: 'Aumentar', icon: TrendingUp, color: 'text-green-500' },
  { value: 'decrease', label: 'Diminuir', icon: TrendingDown, color: 'text-blue-500' },
  { value: 'maintain', label: 'Manter', icon: Minus, color: 'text-yellow-500' },
];

const STATUS_CONFIG = {
  on_track: { label: 'No Alvo', color: 'badge-green', icon: CheckCircle },
  attention: { label: 'Atenção', color: 'badge-yellow', icon: AlertTriangle },
  critical: { label: 'Crítico', color: 'badge-red', icon: XCircle },
  no_data: { label: 'Sem Dados', color: 'badge-gray', icon: Minus },
};

const Indicators = () => {
  const [activeTab, setActiveTab] = useState('list');
  const [indicators, setIndicators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStrategic, setFilterStrategic] = useState('');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  const [periodMode, setPeriodMode] = useState('custom');
  const [periodYear, setPeriodYear] = useState(new Date().getFullYear());
  const [periodUnit, setPeriodUnit] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showMeasurementModal, setShowMeasurementModal] = useState(false);
  const [editingIndicator, setEditingIndicator] = useState(null);
  const [selectedIndicator, setSelectedIndicator] = useState(null);
  const [measurements, setMeasurements] = useState([]);
  const [measurementPeriodFilter, setMeasurementPeriodFilter] = useState({ start_date: '', end_date: '' });
  const [users, setUsers] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    indicator_type: 'quality',
    frequency: 'monthly',
    trend: 'increase',
    unit: '',
    formula: '',
    data_source: '',
    target_value: '',
    minimum_value: '',
    maximum_value: '',
    process: '',
    area: '',
    responsible_id: '',
    is_strategic: false,
  });
  const [measurementData, setMeasurementData] = useState({
    value: '',
    measurement_date: new Date().toISOString().split('T')[0],
    period: '',
    notes: '',
    analysis: '',
  });
  const [saving, setSaving] = useState(false);
  const [stats, setStats] = useState(null);
  const [processes, setProcesses] = useState([]);

  useEffect(() => {
    loadData();
    loadUsers();
    loadProcesses();
  }, [filterType, filterStrategic, filterStartDate, filterEndDate]);

  // Recalculate the date range whenever a preset period (mês/trimestre/semestre/ano) is chosen
  useEffect(() => {
    if (periodMode === 'custom') return;
    const range = getPeriodRange(periodMode, periodYear, periodUnit);
    if (range) {
      setFilterStartDate(range.start);
      setFilterEndDate(range.end);
    }
  }, [periodMode, periodYear, periodUnit]);

  const handlePeriodModeChange = (mode) => {
    setPeriodMode(mode);
    if (mode === 'custom') {
      setFilterStartDate('');
      setFilterEndDate('');
    } else {
      setPeriodUnit(1);
    }
  };

  const handleClearPeriodFilter = () => {
    setPeriodMode('custom');
    setFilterStartDate('');
    setFilterEndDate('');
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterType) params.indicator_type = filterType;
      if (filterStrategic !== '') params.is_strategic = filterStrategic === 'true';
      if (filterStartDate) params.start_date = filterStartDate;
      if (filterEndDate) params.end_date = filterEndDate;

      const [indicatorsRes, statsRes] = await Promise.all([
        indicatorsApi.list(params),
        indicatorsApi.stats(),
      ]);
      setIndicators(indicatorsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const loadProcesses = async () => {
    try {
      const response = await processesApi.listActive();
      setProcesses(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar processos:', error);
    }
  };

  const loadUsers = async () => {
    try {
      const res = await usersApi.list({ is_active: true });
      setUsers(res.data);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const loadMeasurements = async (indicatorId, periodFilter = {}) => {
    try {
      const params = {};
      if (periodFilter.start_date) params.start_date = periodFilter.start_date;
      if (periodFilter.end_date) params.end_date = periodFilter.end_date;
      const res = await indicatorsApi.listMeasurements(indicatorId, params);
      setMeasurements(res.data);
    } catch (error) {
      console.error('Error loading measurements:', error);
    }
  };

  const handleOpenModal = async (indicator = null) => {
    if (indicator) {
      try {
        const res = await indicatorsApi.get(indicator.id);
        const fullIndicator = res.data;
        setEditingIndicator(fullIndicator);
        setFormData({
          name: fullIndicator.name,
          description: fullIndicator.description || '',
          indicator_type: fullIndicator.indicator_type,
          frequency: fullIndicator.frequency,
          trend: fullIndicator.trend,
          unit: fullIndicator.unit || '',
          formula: fullIndicator.formula || '',
          data_source: fullIndicator.data_source || '',
          target_value: fullIndicator.target_value || '',
          minimum_value: fullIndicator.minimum_value || '',
          maximum_value: fullIndicator.maximum_value || '',
          process: fullIndicator.process || '',
          area: fullIndicator.area || '',
          responsible_id: fullIndicator.responsible_id || '',
          is_strategic: fullIndicator.is_strategic || false,
        });
        setShowModal(true);
      } catch (error) {
        toast.error('Erro ao carregar dados do indicador');
      }
    } else {
      setEditingIndicator(null);
      setFormData({
        name: '',
        description: '',
        indicator_type: 'quality',
        frequency: 'monthly',
        trend: 'increase',
        unit: '',
        formula: '',
        data_source: '',
        target_value: '',
        minimum_value: '',
        maximum_value: '',
        process: '',
        area: '',
        responsible_id: '',
        is_strategic: false,
      });
      setShowModal(true);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingIndicator(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name) {
      toast.error('Preencha o nome do indicador');
      return;
    }
    
    setSaving(true);
    
    try {
      const data = {
        ...formData,
        target_value: formData.target_value ? parseFloat(formData.target_value) : null,
        minimum_value: formData.minimum_value ? parseFloat(formData.minimum_value) : null,
        maximum_value: formData.maximum_value ? parseFloat(formData.maximum_value) : null,
        responsible_id: formData.responsible_id || null,
      };

      if (editingIndicator) {
        await indicatorsApi.update(editingIndicator.id, data);
        toast.success('Indicador atualizado!');
      } else {
        await indicatorsApi.create(data);
        toast.success('Indicador criado!');
      }
      handleCloseModal();
      loadData();
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao salvar'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (indicator) => {
    if (!window.confirm(`Excluir indicador "${indicator.code}"?`)) return;
    
    try {
      await indicatorsApi.delete(indicator.id);
      toast.success('Indicador excluído!');
      loadData();
    } catch (error) {
      toast.error('Erro ao excluir');
    }
  };

  const handleViewDetails = async (indicator) => {
    try {
      const res = await indicatorsApi.get(indicator.id);
      setSelectedIndicator(res.data);
      setMeasurementPeriodFilter({ start_date: '', end_date: '' });
      await loadMeasurements(indicator.id);
      setShowDetailModal(true);
    } catch (error) {
      toast.error('Erro ao carregar detalhes');
    }
  };

  const handleMeasurementPeriodChange = (field, value) => {
    const updated = { ...measurementPeriodFilter, [field]: value };
    setMeasurementPeriodFilter(updated);
    if (selectedIndicator) {
      loadMeasurements(selectedIndicator.id, updated);
    }
  };

  const handleClearMeasurementPeriod = () => {
    setMeasurementPeriodFilter({ start_date: '', end_date: '' });
    if (selectedIndicator) {
      loadMeasurements(selectedIndicator.id, {});
    }
  };

  const handleOpenMeasurement = (indicator) => {
    setSelectedIndicator(indicator);
    setMeasurementData({
      value: '',
      measurement_date: new Date().toISOString().split('T')[0],
      period: '',
      notes: '',
      analysis: '',
    });
    setShowMeasurementModal(true);
  };

  const handleSubmitMeasurement = async (e) => {
    e.preventDefault();
    
    if (!measurementData.value) {
      toast.error('Informe o valor');
      return;
    }
    
    setSaving(true);
    
    try {
      await indicatorsApi.createMeasurement(selectedIndicator.id, {
        ...measurementData,
        value: parseFloat(measurementData.value),
      });
      toast.success('Medição registrada!');
      setShowMeasurementModal(false);
      loadData();
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao registrar'));
    } finally {
      setSaving(false);
    }
  };

  const filteredIndicators = indicators.filter(ind =>
    ind.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    ind.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    ind.area?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const StatusBadge = ({ status }) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.no_data;
    const Icon = config.icon;
    return (
      <span className={`badge ${config.color}`}>
        <Icon size={12} className="mr-1" />
        {config.label}
      </span>
    );
  };

  const TrendIcon = ({ trend }) => {
    const config = TRENDS.find(t => t.value === trend) || TRENDS[0];
    const Icon = config.icon;
    return <Icon size={16} className={config.color} />;
  };

  const COLUMNS = [
    { key: 'code', label: 'Código', locked: true, render: (indicator) => (
      <td className="font-mono text-green-400">
        {indicator.code}
        {indicator.is_strategic && (
          <span className="ml-2 text-purple-500" title="Estratégico">★</span>
        )}
      </td>
    ) },
    { key: 'name', label: 'Nome', render: (indicator) => <td><div className="max-w-xs truncate">{indicator.name}</div></td> },
    { key: 'type', label: 'Tipo', render: (indicator) => <td>{INDICATOR_TYPES.find(t => t.value === indicator.indicator_type)?.label || indicator.indicator_type}</td> },
    { key: 'source', label: 'Fonte de Dados', render: (indicator) => <td className="text-sm text-gray-400 max-w-xs truncate" title={indicator.data_source || ''}>{indicator.data_source || '-'}</td> },
    { key: 'frequency', label: 'Frequência', render: (indicator) => <td>{FREQUENCIES.find(f => f.value === indicator.frequency)?.label || indicator.frequency}</td> },
    { key: 'trend', label: 'Tendência', headerClass: 'text-center', render: (indicator) => <td className="text-center"><TrendIcon trend={indicator.trend} /></td> },
    { key: 'target', label: 'Meta', headerClass: 'text-right', render: (indicator) => <td className="text-right">{indicator.target_value != null ? `${indicator.target_value}${indicator.unit ? ` ${indicator.unit}` : ''}` : '-'}</td> },
    { key: 'min', label: 'Mínimo', headerClass: 'text-right', render: (indicator) => <td className="text-right text-yellow-400">{indicator.minimum_value != null ? `${indicator.minimum_value}${indicator.unit ? ` ${indicator.unit}` : ''}` : '-'}</td> },
    { key: 'max', label: 'Máximo', headerClass: 'text-right', render: (indicator) => <td className="text-right text-blue-400">{indicator.maximum_value != null ? `${indicator.maximum_value}${indicator.unit ? ` ${indicator.unit}` : ''}` : '-'}</td> },
    { key: 'current', label: 'Atual', headerClass: 'text-right', render: (indicator) => <td className="text-right font-semibold">{indicator.current_value != null ? `${indicator.current_value}${indicator.unit ? ` ${indicator.unit}` : ''}` : '-'}</td> },
    { key: 'status', label: 'Status', render: (indicator) => <td><StatusBadge status={indicator.status} /></td> },
    { key: 'actions', label: 'Ações', locked: true, headerClass: 'text-right', render: (indicator) => (
      <td className="text-right">
        <div className="flex justify-end gap-2">
          <button
            onClick={() => handleOpenMeasurement(indicator)}
            className="p-1 hover:text-purple-400 transition-colors"
            title="Registrar Medição"
          >
            <Target size={16} />
          </button>
          <button
            onClick={() => handleViewDetails(indicator)}
            className="p-1 hover:text-blue-400 transition-colors"
            title="Ver detalhes"
          >
            <Eye size={16} />
          </button>
          <button
            onClick={() => handleOpenModal(indicator)}
            className="p-1 hover:text-green-400 transition-colors"
            title="Editar"
          >
            <Edit size={16} />
          </button>
          <button
            onClick={() => handleDelete(indicator)}
            className="p-1 hover:text-red-400 transition-colors"
            title="Excluir"
          >
            <Trash2 size={16} />
          </button>
        </div>
      </td>
    ) },
  ];

  const { visibleColumns, visibleKeys, toggleColumn, resetColumns } = useColumnVisibility({ storageKey: 'indicators', columns: COLUMNS });

  return (
    <div className="space-y-6" data-testid="indicators-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Gestão de Indicadores</h1>
          <p className="text-gray-400 text-sm">Monitore e analise KPIs do sistema de gestão</p>
        </div>
        {activeTab === 'list' && <div className="flex items-center gap-2">
          <ColumnSelector columns={COLUMNS} visibleKeys={visibleKeys} onToggle={toggleColumn} onReset={resetColumns} />
          <button
            onClick={() => handleOpenModal()}
            className="btn-primary"
            data-testid="new-indicator-btn"
          >
            <Plus size={20} />
            Novo Indicador
          </button>
        </div>}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-700" role="tablist">
        {[
          { key: 'list', label: 'Indicadores' },
          { key: 'planning', label: 'Planejamento' },
        ].map(tab => (
          <button
            key={tab.key}
            role="tab"
            aria-selected={activeTab === tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === tab.key
                ? 'border-green-500 text-green-400'
                : 'border-transparent text-gray-400 hover:text-white'
            }`}
            data-testid={`tab-${tab.key}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'planning' && <IndicatorPlanning />}

      {activeTab === 'list' && (<>
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="card p-4">
            <p className="text-gray-400 text-sm">Total</p>
            <p className="text-2xl font-bold text-white">{stats.total}</p>
          </div>
          <div className="card p-4">
            <p className="text-gray-400 text-sm">Estratégicos</p>
            <p className="text-2xl font-bold text-purple-500">{stats.strategic}</p>
          </div>
          <div className="card p-4">
            <p className="text-gray-400 text-sm">No Alvo</p>
            <p className="text-2xl font-bold text-green-500">{stats.by_status?.on_track || 0}</p>
          </div>
          <div className="card p-4">
            <p className="text-gray-400 text-sm">Atenção</p>
            <p className="text-2xl font-bold text-yellow-500">{stats.by_status?.attention || 0}</p>
          </div>
          <div className="card p-4">
            <p className="text-gray-400 text-sm">Críticos</p>
            <p className="text-2xl font-bold text-red-500">{stats.by_status?.critical || 0}</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Buscar por código, nome ou área..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input pl-10 w-full"
              data-testid="search-input"
            />
          </div>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="input px-3 w-full sm:w-60"
            data-testid="type-filter"
          >
            <option value="">Todos os Tipos</option>
            {INDICATOR_TYPES.map(t => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
          <select
            value={filterStrategic}
            onChange={(e) => setFilterStrategic(e.target.value)}
            className="input px-3 w-full sm:w-60"
            data-testid="strategic-filter"
          >
            <option value="">Todos</option>
            <option value="true">Estratégicos</option>
            <option value="false">Operacionais</option>
          </select>
          <button onClick={loadData} className="btn-secondary" data-testid="refresh-btn">
            <RefreshCw size={20} />
          </button>
        </div>
        <div className="flex flex-col sm:flex-row flex-wrap items-start sm:items-center gap-2 mt-4 pt-4 border-t border-gray-700">
          <span className="text-gray-400 text-sm whitespace-nowrap">Período de ocorrências:</span>
          <select
            value={periodMode}
            onChange={(e) => handlePeriodModeChange(e.target.value)}
            className="input px-3 w-full sm:w-44"
            data-testid="period-mode-filter"
          >
            {PERIOD_MODES.map(p => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>

          {periodMode === 'custom' ? (
            <>
              <input
                type="date"
                value={filterStartDate}
                onChange={(e) => setFilterStartDate(e.target.value)}
                className="input px-3 w-full sm:w-48"
                data-testid="period-start-filter"
              />
              <span className="text-gray-400 text-sm">até</span>
              <input
                type="date"
                value={filterEndDate}
                onChange={(e) => setFilterEndDate(e.target.value)}
                className="input px-3 w-full sm:w-48"
                data-testid="period-end-filter"
              />
            </>
          ) : (
            <>
              {periodMode === 'month' && (
                <select
                  value={periodUnit}
                  onChange={(e) => setPeriodUnit(Number(e.target.value))}
                  className="input px-3 w-full sm:w-40"
                  data-testid="period-unit-filter"
                >
                  {MONTHS.map(m => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              )}
              {periodMode === 'quarter' && (
                <select
                  value={periodUnit}
                  onChange={(e) => setPeriodUnit(Number(e.target.value))}
                  className="input px-3 w-full sm:w-56"
                  data-testid="period-unit-filter"
                >
                  {QUARTERS.map(q => (
                    <option key={q.value} value={q.value}>{q.label}</option>
                  ))}
                </select>
              )}
              {periodMode === 'semester' && (
                <select
                  value={periodUnit}
                  onChange={(e) => setPeriodUnit(Number(e.target.value))}
                  className="input px-3 w-full sm:w-56"
                  data-testid="period-unit-filter"
                >
                  {SEMESTERS.map(s => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              )}
              <select
                value={periodYear}
                onChange={(e) => setPeriodYear(Number(e.target.value))}
                className="input px-3 w-full sm:w-32"
                data-testid="period-year-filter"
              >
                {Array.from({ length: 8 }, (_, i) => new Date().getFullYear() - 5 + i).map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </>
          )}

          {(filterStartDate || filterEndDate) && (
            <button
              type="button"
              onClick={handleClearPeriodFilter}
              className="text-gray-400 hover:text-white text-sm underline"
              data-testid="period-filter-clear"
            >
              Limpar período
            </button>
          )}

          {periodMode !== 'custom' && filterStartDate && filterEndDate && (
            <span className="text-gray-500 text-xs">
              ({new Date(filterStartDate + 'T00:00:00').toLocaleDateString('pt-BR')} - {new Date(filterEndDate + 'T00:00:00').toLocaleDateString('pt-BR')})
            </span>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="animate-spin text-green-500" size={32} />
          </div>
        ) : filteredIndicators.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <p>Nenhum indicador encontrado</p>
          </div>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  {visibleColumns.map(col => (
                    <th key={col.key} className={col.headerClass || ''}>{col.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredIndicators.map((indicator) => (
                  <tr key={indicator.id} data-testid={`indicator-row-${indicator.id}`}>
                    {visibleColumns.map(col => (
                      <Fragment key={col.key}>{col.render(indicator)}</Fragment>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      </>)}

      {/* Create/Edit Modal */}
      {showModal && (
        <>
          <div className="modal-backdrop" />
          <div className="modal-content p-6 max-w-2xl">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white">
                {editingIndicator ? 'Editar Indicador' : 'Novo Indicador'}
              </h2>
              <button onClick={handleCloseModal} className="text-gray-400 hover:text-white">
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-300 mb-2">Nome *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="input w-full"
                    placeholder="Nome do indicador"
                    data-testid="indicator-name-input"
                  />
                </div>
                <div className="flex items-center gap-2 pt-6">
                  <input
                    type="checkbox"
                    id="is_strategic"
                    checked={formData.is_strategic}
                    onChange={(e) => setFormData({...formData, is_strategic: e.target.checked})}
                    className="w-4 h-4"
                  />
                  <label htmlFor="is_strategic" className="text-gray-300">Estratégico</label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Descrição</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="input w-full"
                  rows={2}
                  placeholder="Descrição do indicador"
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Tipo</label>
                  <select
                    value={formData.indicator_type}
                    onChange={(e) => setFormData({...formData, indicator_type: e.target.value})}
                    className="input w-full"
                  >
                    {INDICATOR_TYPES.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Frequência</label>
                  <select
                    value={formData.frequency}
                    onChange={(e) => setFormData({...formData, frequency: e.target.value})}
                    className="input w-full"
                  >
                    {FREQUENCIES.map(f => (
                      <option key={f.value} value={f.value}>{f.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Tendência Desejada</label>
                  <select
                    value={formData.trend}
                    onChange={(e) => setFormData({...formData, trend: e.target.value})}
                    className="input w-full"
                  >
                    {TRENDS.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Unidade</label>
                  <select
                    value={formData.unit}
                    onChange={(e) => setFormData({...formData, unit: e.target.value})}
                    className="input w-full"
                  >
                    <option value="">— selecione —</option>
                    <optgroup label="Geral">
                      <option value="%">% (porcentagem)</option>
                      <option value="un">un (unidade)</option>
                      <option value="R$">R$ (reais)</option>
                    </optgroup>
                    <optgroup label="Massa">
                      <option value="kg">kg (quilograma)</option>
                      <option value="g">g (grama)</option>
                      <option value="mg">mg (miligrama)</option>
                      <option value="t">t (tonelada)</option>
                    </optgroup>
                    <optgroup label="Volume">
                      <option value="L">L (litro)</option>
                      <option value="mL">mL (mililitro)</option>
                      <option value="m³">m³ (metro cúbico)</option>
                    </optgroup>
                    <optgroup label="Comprimento / Área">
                      <option value="m">m (metro)</option>
                      <option value="cm">cm (centímetro)</option>
                      <option value="mm">mm (milímetro)</option>
                      <option value="km">km (quilômetro)</option>
                      <option value="m²">m² (metro quadrado)</option>
                    </optgroup>
                    <optgroup label="Tempo">
                      <option value="h">h (hora)</option>
                      <option value="min">min (minuto)</option>
                      <option value="s">s (segundo)</option>
                    </optgroup>
                    <optgroup label="Energia / Temperatura">
                      <option value="kWh">kWh (quilowatt-hora)</option>
                      <option value="°C">°C (grau Celsius)</option>
                    </optgroup>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Meta</label>
                  <input
                    type="number"
                    step="any"
                    value={formData.target_value}
                    onChange={(e) => setFormData({...formData, target_value: e.target.value})}
                    className="input w-full font-semibold text-white text-base"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Mínimo</label>
                  <input
                    type="number"
                    step="any"
                    value={formData.minimum_value}
                    onChange={(e) => setFormData({...formData, minimum_value: e.target.value})}
                    className="input w-full font-semibold text-white text-base"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Máximo</label>
                  <input
                    type="number"
                    step="any"
                    value={formData.maximum_value}
                    onChange={(e) => setFormData({...formData, maximum_value: e.target.value})}
                    className="input w-full font-semibold text-white text-base"
                    placeholder="0"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Fórmula de Cálculo</label>
                <input
                  type="text"
                  value={formData.formula}
                  onChange={(e) => setFormData({...formData, formula: e.target.value})}
                  className="input w-full"
                  placeholder="Ex: (Produzidos / Planejados) x 100"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Área</label>
                  <input
                    type="text"
                    value={formData.area}
                    onChange={(e) => setFormData({...formData, area: e.target.value})}
                    className="input w-full"
                    placeholder="Ex: Produção, Qualidade..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Processo</label>
                  <select
                    value={formData.process}
                    onChange={(e) => setFormData({...formData, process: e.target.value})}
                    className="input w-full"
                  >
                    <option value="">Selecione um processo</option>
                    {processes.map(p => (
                      <option key={p.id} value={p.name}>{p.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Fonte de Dados</label>
                  <input
                    type="text"
                    value={formData.data_source}
                    onChange={(e) => setFormData({...formData, data_source: e.target.value})}
                    className="input w-full"
                    placeholder="Ex: ERP, Planilha, Sistema..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Responsável</label>
                  <select
                    value={formData.responsible_id}
                    onChange={(e) => setFormData({...formData, responsible_id: e.target.value})}
                    className="input w-full"
                  >
                    <option value="">Selecione...</option>
                    {users.map(u => (
                      <option key={u.id} value={u.id}>{u.full_name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
                <button type="button" onClick={handleCloseModal} className="btn-secondary">
                  Cancelar
                </button>
                <button type="submit" className="btn-primary" disabled={saving} data-testid="indicator-submit-btn">
                  {saving ? <Loader2 className="animate-spin" size={20} /> : editingIndicator ? 'Salvar' : 'Criar'}
                </button>
              </div>
            </form>
          </div>
        </>
      )}

      {/* Measurement Modal */}
      {showMeasurementModal && selectedIndicator && (
        <>
          <div className="modal-backdrop" />
          <div className="modal-content p-6 max-w-md">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold text-white">Registrar Medição</h2>
                <p className="text-gray-400 text-sm">{selectedIndicator.code} - {selectedIndicator.name}</p>
              </div>
              <button onClick={() => setShowMeasurementModal(false)} className="text-gray-400 hover:text-white">
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={handleSubmitMeasurement} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Valor *</label>
                  <input
                    type="number"
                    step="any"
                    value={measurementData.value}
                    onChange={(e) => setMeasurementData({...measurementData, value: e.target.value})}
                    className="input w-full"
                    placeholder="0"
                    data-testid="measurement-value-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Data *</label>
                  <input
                    type="date"
                    value={measurementData.measurement_date}
                    onChange={(e) => setMeasurementData({...measurementData, measurement_date: e.target.value})}
                    className="input w-full"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Período</label>
                <input
                  type="text"
                  value={measurementData.period}
                  onChange={(e) => setMeasurementData({...measurementData, period: e.target.value})}
                  className="input w-full"
                  placeholder="Ex: 2025-01, 2025-Q1..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Observações</label>
                <textarea
                  value={measurementData.notes}
                  onChange={(e) => setMeasurementData({...measurementData, notes: e.target.value})}
                  className="input w-full"
                  rows={2}
                  placeholder="Notas sobre a medição"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Análise do Resultado</label>
                <textarea
                  value={measurementData.analysis}
                  onChange={(e) => setMeasurementData({...measurementData, analysis: e.target.value})}
                  className="input w-full"
                  rows={2}
                  placeholder="Análise do desempenho"
                />
              </div>

              <div className="bg-gray-800 rounded-lg p-3 text-sm">
                <p className="text-gray-400">Meta: <span className="text-white font-semibold">{selectedIndicator.target_value || '-'} {selectedIndicator.unit}</span></p>
                <p className="text-gray-400">Valor Atual: <span className="text-white font-semibold">{selectedIndicator.current_value || '-'} {selectedIndicator.unit}</span></p>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
                <button type="button" onClick={() => setShowMeasurementModal(false)} className="btn-secondary">
                  Cancelar
                </button>
                <button type="submit" className="btn-primary" disabled={saving} data-testid="measurement-submit-btn">
                  {saving ? <Loader2 className="animate-spin" size={20} /> : 'Registrar'}
                </button>
              </div>
            </form>
          </div>
        </>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedIndicator && (
        <>
          <div className="modal-backdrop" />
          <div className="modal-content p-6 max-w-3xl">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold text-white">{selectedIndicator.code}</h2>
                <p className="text-gray-400">{selectedIndicator.name}</p>
              </div>
              <button onClick={() => setShowDetailModal(false)} className="text-gray-400 hover:text-white">
                <X size={24} />
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="flex gap-2 flex-wrap">
                <StatusBadge status={selectedIndicator.status || 'no_data'} />
                <span className="badge badge-gray">
                  {INDICATOR_TYPES.find(t => t.value === selectedIndicator.indicator_type)?.label}
                </span>
                <span className="badge badge-gray">
                  {FREQUENCIES.find(f => f.value === selectedIndicator.frequency)?.label}
                </span>
                {selectedIndicator.is_strategic && (
                  <span className="badge badge-purple">★ Estratégico</span>
                )}
              </div>

              {selectedIndicator.description && (
                <div>
                  <p className="text-gray-400 text-sm">Descrição</p>
                  <p className="text-white">{selectedIndicator.description}</p>
                </div>
              )}

              <div className="grid grid-cols-4 gap-4 bg-gray-800 rounded-lg p-4">
                <div className="text-center">
                  <p className="text-gray-400 text-xs">Meta</p>
                  <p className="text-xl font-bold text-white">{selectedIndicator.target_value || '-'}</p>
                  <p className="text-gray-500 text-xs">{selectedIndicator.unit}</p>
                </div>
                <div className="text-center">
                  <p className="text-gray-400 text-xs">Atual</p>
                  <p className="text-xl font-bold text-green-500">{selectedIndicator.current_value || '-'}</p>
                  <p className="text-gray-500 text-xs">{selectedIndicator.unit}</p>
                </div>
                <div className="text-center">
                  <p className="text-gray-400 text-xs">Mínimo</p>
                  <p className="text-xl font-bold text-yellow-500">{selectedIndicator.minimum_value || '-'}</p>
                  <p className="text-gray-500 text-xs">{selectedIndicator.unit}</p>
                </div>
                <div className="text-center">
                  <p className="text-gray-400 text-xs">Máximo</p>
                  <p className="text-xl font-bold text-blue-500">{selectedIndicator.maximum_value || '-'}</p>
                  <p className="text-gray-500 text-xs">{selectedIndicator.unit}</p>
                </div>
              </div>

              {selectedIndicator.formula && (
                <div>
                  <p className="text-gray-400 text-sm">Fórmula</p>
                  <p className="text-white font-mono bg-gray-800 p-2 rounded">{selectedIndicator.formula}</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-gray-400 text-sm">Área</p>
                  <p className="text-white">{selectedIndicator.area || '-'}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Processo</p>
                  <p className="text-white">{selectedIndicator.process || '-'}</p>
                </div>
              </div>

              {/* Measurements History */}
              <div>
                <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                  <h3 className="text-white font-semibold">Histórico de Medições</h3>
                  <div className="flex items-center gap-2 flex-wrap">
                    <label className="text-gray-400 text-xs">De</label>
                    <input
                      type="date"
                      value={measurementPeriodFilter.start_date}
                      onChange={(e) => handleMeasurementPeriodChange('start_date', e.target.value)}
                      className="input w-40 py-1 text-sm"
                      data-testid="measurement-period-start"
                    />
                    <label className="text-gray-400 text-xs">Até</label>
                    <input
                      type="date"
                      value={measurementPeriodFilter.end_date}
                      onChange={(e) => handleMeasurementPeriodChange('end_date', e.target.value)}
                      className="input w-40 py-1 text-sm"
                      data-testid="measurement-period-end"
                    />
                    {(measurementPeriodFilter.start_date || measurementPeriodFilter.end_date) && (
                      <button
                        type="button"
                        onClick={handleClearMeasurementPeriod}
                        className="text-gray-400 hover:text-white text-xs underline"
                      >
                        Limpar
                      </button>
                    )}
                  </div>
                </div>
                {measurements.length === 0 ? (
                  <p className="text-gray-400 text-sm py-2">Nenhuma medição encontrada para o período selecionado.</p>
                ) : (
                  <div className="max-h-48 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-800 sticky top-0">
                        <tr>
                          <th className="text-left p-2 text-gray-400">Data</th>
                          <th className="text-right p-2 text-gray-400">Valor</th>
                          <th className="text-center p-2 text-gray-400">Status</th>
                          <th className="text-left p-2 text-gray-400">Período</th>
                        </tr>
                      </thead>
                      <tbody>
                        {measurements.map((m) => (
                          <tr key={m.id} className="border-b border-gray-700">
                            <td className="p-2 text-white">{new Date(m.measurement_date).toLocaleDateString('pt-BR')}</td>
                            <td className="p-2 text-right text-white font-semibold">{m.value}</td>
                            <td className="p-2 text-center">
                              {m.is_on_target === true && <CheckCircle size={16} className="text-green-500 inline" />}
                              {m.is_on_target === false && <XCircle size={16} className="text-red-500 inline" />}
                              {m.is_on_target === null && <Minus size={16} className="text-gray-500 inline" />}
                            </td>
                            <td className="p-2 text-gray-400">{m.period || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
                <button onClick={() => setShowDetailModal(false)} className="btn-secondary">
                  Fechar
                </button>
                <button 
                  onClick={() => { setShowDetailModal(false); handleOpenMeasurement(selectedIndicator); }} 
                  className="btn-primary"
                >
                  <Target size={16} />
                  Nova Medição
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Indicators;
